import { Select, Stack, Text } from "@mantine/core";
import type { ParseKeys } from "i18next";
import * as React from "react";
import { useTranslation } from "react-i18next";
import {
  getLanguagePreference,
  setLanguagePreference,
  supportedLocales,
  type LanguagePreference,
  type SupportedLocale,
} from "../i18n";
import { NavBarContainer } from "./NavBarContainer";

function isLanguagePreference(value: string): value is LanguagePreference {
  return value === "browser" || (supportedLocales as readonly string[]).includes(value);
}

const languageOptionOrder: SupportedLocale[] = [
  "en-US",
  "es-MX",
  "de-DE",
  "fr-FR",
];

const languageLabels: Record<SupportedLocale, ParseKeys> = {
  "en-US": "settings.languageEnglish",
  "es-MX": "settings.languageSpanish",
  "de-DE": "settings.languageGerman",
  "fr-FR": "settings.languageFrench",
};

export function SettingsPage() {
  const { t } = useTranslation();
  const [languagePreference, setLanguagePreferenceState] =
    React.useState<LanguagePreference>(() => getLanguagePreference());

  const handleLanguageChange = async (value: string | null) => {
    if (!value || !isLanguagePreference(value)) {
      return;
    }
    setLanguagePreferenceState(value);
    await setLanguagePreference(value);
  };

  return (
    <NavBarContainer title={t("settings.title")}>
      <Stack gap="md" maw={420}>
        <Text size="sm" c="dimmed">
          {t("settings.languageDescription")}
        </Text>
        <Select
          label={t("settings.language")}
          value={languagePreference}
          onChange={handleLanguageChange}
          data={[
            { value: "browser", label: t("settings.languageBrowser") },
            ...languageOptionOrder.map((locale) => ({
              value: locale,
              label: t(languageLabels[locale]),
            })),
          ]}
          allowDeselect={false}
        />
      </Stack>
    </NavBarContainer>
  );
}
